import type {
  ListExecutionsRequest,
  ListExecutionsResponse,
  PlaywrightExecutionDetail,
  TestHistoryRequest,
  TestHistoryResponse,
  BatchTestHistoryRequest,
  BatchTestHistoryResponse,
  StartBuildRequest,
  StartBuildResponse,
  GetBuildStatusResponse,
  StopBuildResponse,
  ListBuildsRequest,
  ListRunningBuildsResponse,
  AnalyzeTestRequest,
  AnalyzeTestResponse,
  CompleteBuildStatus,
  CompleteExecutionResponse,
  ListTestFilesResponse,
  GetEnvironmentInfoResponse
} from './test-executions.types'
import { v3ApiHttp } from '@/services/api/tattle-http'

/**
 * Test executions API service
 */
export const testExecutionsAPI = {
  /**
   * List Playwright test executions
   */
  async listPlaywrightExecutions(params: ListExecutionsRequest): Promise<ListExecutionsResponse> {
    const searchParams = new URLSearchParams()

    if (params.limit !== undefined) {
      searchParams.append('limit', params.limit.toString())
    }
    if (params.offset !== undefined) {
      searchParams.append('offset', params.offset.toString())
    }
    if (params.startDate) {
      searchParams.append('startDate', params.startDate)
    }
    if (params.endDate) {
      searchParams.append('endDate', params.endDate)
    }
    if (params.status && params.status.length > 0) {
      params.status.forEach(s => searchParams.append('status', s))
    }
    if (params.search) {
      searchParams.append('search', params.search)
    }

    const queryString = searchParams.toString()
    const url = `/test-executions/playwright/executions?${queryString}`
    const response = await v3ApiHttp.get<ListExecutionsResponse>(url)
    return response.data
  },

  /**
   * Get Playwright test execution details
   */
  async getPlaywrightExecution(executionId: string): Promise<PlaywrightExecutionDetail> {
    const url = `/test-executions/playwright/executions/${executionId}`
    const response = await v3ApiHttp.get<PlaywrightExecutionDetail>(url)
    return response.data
  },

  /**
   * Get test execution history for a specific test
   */
  async getTestHistory(params: TestHistoryRequest): Promise<TestHistoryResponse> {
    const searchParams = new URLSearchParams()

    searchParams.append('testTitle', params.testTitle)
    searchParams.append('testFile', params.testFile)
    searchParams.append('project', params.project)

    if (params.limit !== undefined) {
      searchParams.append('limit', params.limit.toString())
    }

    const queryString = searchParams.toString()
    const url = `/test-executions/playwright/tests/history?${queryString}`
    const response = await v3ApiHttp.get<TestHistoryResponse>(url)
    return response.data
  },

  /**
   * Get test execution history for multiple tests in a single batch request
   */
  async getBatchTestHistory(params: BatchTestHistoryRequest): Promise<BatchTestHistoryResponse> {
    const url = '/test-executions/playwright/tests/history/batch'
    const response = await v3ApiHttp.post<BatchTestHistoryResponse>(url, params)
    return response.data
  },

  /**
   * Get presigned URL for Playwright trace file
   * @param traceUrl - The S3 URL of the trace file from the execution data
   */
  async getTracePresignedUrl(traceUrl: string): Promise<string> {
    const url = `/test-executions/playwright/trace?traceUrl=${encodeURIComponent(traceUrl)}`
    const response = await v3ApiHttp.get<string>(url)
    return response.data
  },

  /**
   * Delete a Playwright test execution
   */
  async deletePlaywrightExecution(executionId: string): Promise<void> {
    const url = `/test-executions/playwright/executions/${executionId}`
    await v3ApiHttp.delete(url)
  },

  /**
   * Start a new Playwright test execution in CodeBuild
   */
  async startBuild(request: StartBuildRequest): Promise<StartBuildResponse> {
    const url = '/test-executions/playwright/builds'
    const response = await v3ApiHttp.post<StartBuildResponse>(url, request)
    return response.data
  },

  /**
   * Get the status of a CodeBuild build
   * @param buildId - The build ID to check status for
   * @param isBatch - Whether this is a batch build (uses different AWS API)
   */
  async getBuildStatus(buildId: string, isBatch?: boolean): Promise<GetBuildStatusResponse> {
    const searchParams = new URLSearchParams()
    if (isBatch !== undefined) {
      searchParams.append('isBatch', isBatch.toString())
    }
    const queryString = searchParams.toString()
    const url = `/test-executions/playwright/builds/${encodeURIComponent(buildId)}${queryString ? `?${queryString}` : ''}`
    const response = await v3ApiHttp.get<GetBuildStatusResponse>(url)
    return response.data
  },

  /**
   * Stop a running CodeBuild build
   */
  async stopBuild(buildId: string): Promise<StopBuildResponse> {
    const url = `/test-executions/playwright/builds/${encodeURIComponent(buildId)}/stop`
    const response = await v3ApiHttp.post<StopBuildResponse>(url)
    return response.data
  },

  /**
   * List CodeBuild builds for tattle-playwright
   */
  async listBuilds(params: ListBuildsRequest = {}): Promise<ListRunningBuildsResponse> {
    const searchParams = new URLSearchParams()

    if (params.limit !== undefined) {
      searchParams.append('limit', params.limit.toString())
    }
    if (params.status && params.status.length > 0) {
      params.status.forEach(s => searchParams.append('status', s))
    }
    if (params.environment) {
      searchParams.append('environment', params.environment)
    }
    if (params.branch) {
      searchParams.append('branch', params.branch)
    }

    const queryString = searchParams.toString()
    const url = `/test-executions/playwright/builds${queryString ? `?${queryString}` : ''}`
    const response = await v3ApiHttp.get<ListRunningBuildsResponse>(url)
    return response.data
  },

  /**
   * Analyze a failed test using AI
   *
   * Analyzes a specific failed test to identify:
   * - Failure category (infrastructure, test-code, application-bug, flaky, timeout, data-issue)
   * - Severity level (critical, high, medium, low)
   * - Root cause explanation
   * - Suggested fix approach
   * - Supporting evidence
   *
   * @param request - Analysis request with test details and error info
   * @returns Analysis results with category, severity, root cause, and suggested fix
   */
  async analyzeTest(request: AnalyzeTestRequest): Promise<AnalyzeTestResponse> {
    const url = '/test-executions/playwright/analyze'
    const response = await v3ApiHttp.post<AnalyzeTestResponse>(url, request)
    return response.data
  },

  /**
   * Manually complete a Playwright test execution
   *
   * Use this when an execution is stuck (e.g., CodeBuild completion event didn't fire)
   * or you know the test is done and don't want to wait.
   *
   * @param executionId - The execution ID to complete
   * @param environment - The environment (dev, staging)
   * @param buildStatus - The final build status (defaults to STOPPED)
   * @returns Completion confirmation
   */
  async completeExecution(
    executionId: string,
    environment: string,
    buildStatus: CompleteBuildStatus = 'STOPPED'
  ): Promise<CompleteExecutionResponse> {
    const url = `/test-executions/playwright/executions/${encodeURIComponent(executionId)}/complete`
    const response = await v3ApiHttp.post<CompleteExecutionResponse>(url, {
      environment,
      finishedAt: new Date().toISOString(),
      buildStatus
    })
    return response.data
  },

  /**
   * List available Playwright test files from Bitbucket
   *
   * Fetches test files from the tattle-playwright repository via Bitbucket API.
   * Results are cached server-side to avoid rate limits.
   *
   * @param project - Optional project name to filter tests (e.g., 'tattle-api')
   * @returns List of test files with path, name, and project
   */
  async listTestFiles(project?: string): Promise<ListTestFilesResponse> {
    const searchParams = new URLSearchParams()
    if (project) {
      searchParams.append('project', project)
    }
    const queryString = searchParams.toString()
    const url = `/test-executions/playwright/tests${queryString ? `?${queryString}` : ''}`
    const response = await v3ApiHttp.get<ListTestFilesResponse>(url)
    return response.data
  },

  /**
   * Get environment info for the current API environment
   *
   * Returns the environment name and detected branch based on the deployed
   * tattle-api Docker image version. Use this to preview what will be sent
   * when triggering a build from ADX.
   *
   * @returns Environment info with detected branch
   */
  async getEnvironmentInfo(): Promise<GetEnvironmentInfoResponse> {
    const url = '/test-executions/playwright/environment-info'
    const response = await v3ApiHttp.get<GetEnvironmentInfoResponse>(url)
    return response.data
  }
}
